// This is a catch-all route.
// It is the entry point for handling entity routes from Drupal.
import { GetStaticPropsResult } from 'next';
import {DrupalNode, DrupalTaxonomyTerm} from 'next-drupal';
import { DrupalJsonApiParams } from 'drupal-jsonapi-params';

import { getMenus } from '../lib/get-menus';
import { LayoutProps } from '../components/layout';
import { drupal } from '../lib/drupal';
import {
  BlurMap,
  DrupalMainMenuLinkContent,
  DrupalWebform,
} from "../types/hygiena-types";
import {getBlurMap} from "../lib/get-blur-map";
import {testApiCompatibility} from "../lib/test-api-compatibility";

// Entity Processing
import {NodeKeyValuePageSlug} from "../lib/slug/node--key-value-page-slug";

// Page Types
import {purgeSlug} from "../lib/purge-slug";
import {getAlertBanner} from "../lib/get-alert-banner";
import {getDictionary} from "../lib/dictionaries";
import {SearchPage} from "../components/templates/key_value_page/key_value_page--search";

// If there is no revalidate ttl stored in config, default to 15 minutes.
export const REVALIDATE_TTL = process?.env?.REVALIDATE_TTL ?? "900";

// List of all the entity types handled by this route.
export const ENTITY_TYPES = [
  'node--page',
  'node--article',
  'node--event',
  'node--key_value_page',
  'node--press_release',
  'node--product',
  'node--landing_page',
  'taxonomy_term--product_type',
  'taxonomy_term--product_class',
  'taxonomy_term--industry_segments',
];

interface EntityPageProps extends LayoutProps {
  entity: DrupalNode | DrupalTaxonomyTerm,
  additionalContent?: any,
  entityInfo?: {
    id?: string,
    type?: string,
  },
  blurMap?: BlurMap,
  metatag?: any,
  newsletterPad?: boolean,
  customMenu?: DrupalMainMenuLinkContent[],
  hideUtilityMenu,
  hideFooterMenu,
  logoScrollToTop,
}

export default function EntityPage({entity, additionalContent}: EntityPageProps) {
  return (
    <SearchPage node={entity as DrupalNode} {...additionalContent?.props}/>
  );
}

export async function getStaticProps(context): Promise<GetStaticPropsResult<EntityPageProps>> {
  // Find a matching path from Drupal from context.
  const start = performance.now();
  const NEXT_BUILD_MODE = process.env?.["NEXT_BUILD_MODE"] ?? false;
  context.params = {slug: 'search'};
  const path = await drupal.translatePathFromContext(context);
  let displayContactUsForm = false;
  let customMenu: DrupalMainMenuLinkContent[] = [];
  let contactUsFormTitle = '';
  let newsletterPad = false;
  let hideUtilityMenu = false;
  let hideFooterMenu = false;
  let logoScrollToTop = false;

  if (process.env.NODE_ENV == 'development') {
    try {
      await testApiCompatibility(ENTITY_TYPES, drupal);
    }
    catch (error) {
      console.log(error);
    }
  }

  if (!path) {
    let slug = context?.params?.slug?.[0] ? context.params.slug.join('/') : 'unknown';
    console.log(`Failed to fetch resource: path not found for slug: ${slug}.`);
    if (!NEXT_BUILD_MODE) {
      return {
        redirect: {
          destination: '/',
          statusCode: 301,
        },
      };
    }
    else {
      return {
        notFound: true,
        revalidate: 60,
      };
    }
  }

  const type = path?.jsonapi?.resourceName ?? '';

  let additionalContent = {} as any;
  const params = new DrupalJsonApiParams();

  if (context?.preview) {
    params.addCustomParam({'resourceVersion': context?.previewData?.resourceVersion});
  }

  params.addInclude([
    'field_kvp_hero_image.image',
  ]);

  let entity: DrupalNode|DrupalTaxonomyTerm|null = null;
  // We always get the entity via an initial call, any subsequent requests for required
  // data should be amalgamated into a sub-request to prevent hammering the server.
  try {
    entity = await drupal.getResourceFromContext<
      DrupalNode | DrupalTaxonomyTerm
    >(type, context, {
      params: params.getQueryObject(),
    });
  }
  catch (error) {
    console.log(`Failed to fetch resource: ${path?.jsonapi?.individual ?? 'unknown'}`);
  }
  if (!entity && context.locale !== context.defaultLocale && path?.entity?.uuid) {
    // If a resource doesn't have a translation, fetching will fail in this case, we'll use the site default language, to account for
    // the lack of current translation. This will prevent build errors.
    entity = await drupal.getResource<
      DrupalNode | DrupalTaxonomyTerm
    >(type, path?.entity?.uuid, {
      params: params.getQueryObject(),
    });
  }

  // At this point, we know the path exists and it points to a resource. If we
  // receive an error, it means something went wrong on Drupal. Let's log an
  // error, so we can see what's going on locally and return not found, so
  // we don't break the build process.
  if (!entity) {
    console.log(`Failed to fetch resource: ${path?.jsonapi?.individual ?? ''}`);
    return {
      notFound: true,
      revalidate: parseInt(REVALIDATE_TTL),
    };
  }

  // If we're not in preview mode and the resource is not published,
  // Return page not found.
  if (!context.preview && entity?.status === false) {
    // Purge if not in build mode.
    if (!NEXT_BUILD_MODE) {
      if (context?.params?.slug) {
        let purgePath = `${context?.locale ?? context?.defaultLocale}/${context?.params?.slug?.join('/')}`;
        await purgeSlug(purgePath);
      }
    }
    return {
      notFound: true,
      revalidate: parseInt(REVALIDATE_TTL),
    };
  }

  const translatedUrls = entity?.translated_urls ?? null;
  if (type === 'node--key_value_page') {
    ({displayContactUsForm, contactUsFormTitle} = await NodeKeyValuePageSlug(entity as DrupalNode, additionalContent, context));
  }

  // The Newsletter is discontinued, and the Drupal Webform for ContactUs
  // is pending CRM integration. Currently, we are only using an ActOn Form
  // we'll simply pass through a title stub if we're enabling the contact
  // us form.
  let contactUs: DrupalWebform|null = null;
  if (displayContactUsForm) {
    contactUs = {
      title: contactUsFormTitle ?? '',
    } as DrupalWebform;
  }

  if (!NEXT_BUILD_MODE) {
    const end = performance.now();
    console.log(`Slug /search took ${((end - start) / 1000).toFixed(2)} seconds to build.`)
  }

  return {
    props: {
      entity: entity,
      entityInfo: {
        id: entity.id,
        type: entity.type
      },
      additionalContent,
      alertBanner: await getAlertBanner(context),
      webforms: {
        contactUs,
      },
      menus: await getMenus(context),
      blurMap: await getBlurMap(),
      metatag: entity?.metatag,
      newsletterPad: !newsletterPad,
      customMenu,
      hideUtilityMenu,
      hideFooterMenu,
      logoScrollToTop,
      translatedUrls,
      dictionary: await getDictionary(context?.locale),
    },
    revalidate: parseInt(REVALIDATE_TTL),
  };
}
